import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { DEMAND_STRATEGY_CURRENT_PLANNING_YEAR_BASE_URL } from 'helpers/constants'
import { initGrowthbookWithToken } from '../growthbook'

/**
 * PlanningYearContext
 * Fetches the current planning year once and provides it app-wide.
 */
const PlanningYearContext = createContext({ planningYear: null, isLoading: true, error: null })

export function PlanningYearProvider({ children }) {
  const [planningYear, setPlanningYear] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const { authState, oktaAuth } = useOktaAuth()

  useEffect(() => {
    let isMounted = true
    const fetchYear = async (token) => {
      if (!token) return
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetch(`${DEMAND_STRATEGY_CURRENT_PLANNING_YEAR_BASE_URL}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`)
        const result = await response.json()
        if (isMounted) {
          const year = result?.currentPlanningYear || result?.planningYear || result?.year || null
          setPlanningYear(year ? Number(year) : null)
        }
      } catch (e) {
        if (isMounted) setError(e)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    // Only attempt once authenticated
    if (authState?.isAuthenticated) {
      const sessionToken = sessionStorage.getItem('auth_token')
      const oktaToken = authState?.accessToken?.accessToken || oktaAuth?.getAccessToken?.()
      const token = sessionToken || oktaToken
      // Initialize GrowthBook once token is available
      initGrowthbookWithToken(token)
      fetchYear(token)
    } else {
      // Not authenticated yet
      setIsLoading(false)
    }

    // Optional: listen for auth_token_ready if some other part of app dispatches it
    const onAuthReady = () => {
      const sessionToken = sessionStorage.getItem('auth_token')
      if (sessionToken) fetchYear(sessionToken)
    }
    window.addEventListener('auth_token_ready', onAuthReady)

    return () => {
      isMounted = false
      window.removeEventListener('auth_token_ready', onAuthReady)
    }
  }, [authState?.isAuthenticated])

  // Generate past years array dynamically based on planning year
  const pastYears = useMemo(() => {
    const currentYear = planningYear ?? new Date().getFullYear()
    return Array.from({ length: 5 }, (_, index) => {
      const year = currentYear - index - 1
      return {
        displayText: year.toString(),
        id: index.toString(),
      }
    })
  }, [planningYear])

  const value = useMemo(
    () => ({ planningYear, isLoading, error, pastYears }),
    [planningYear, isLoading, error, pastYears]
  )

  return <PlanningYearContext.Provider value={value}>{children}</PlanningYearContext.Provider>
}

export const usePlanningYear = () => useContext(PlanningYearContext)

export default PlanningYearContext
